import React from 'react';
import { graphql, Link, Page } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { GatsbyImageType } from 'types/common';
import LatestArticles, { LatestArticleType } from 'components/latest-articles/latest-articles';
import { createArticleUrl } from 'helpers/utils';
import { SubscriptionBanner } from 'components/subscription-banner';
import ShortUnderline from 'assets/icons/shortUnderline.svg';
import { Layout } from 'components/layout';
import SearchProfession from './search-profession';
import * as styles from './profession.module.scss';
import { SmartLink } from 'components/smart-link';
import { PageContext } from 'gatsby/internal';

type ProfessionProps = {
  pageContext: PageContext & {
    subscribeName: string,
    subscribeLink: string
  }
  data: {
    strapiProfession: {
      name: string
      bannerDescription: string
      searchPlaceholder?: string
      image: GatsbyImageType
      links: {
        name: string
        link: string
      }[]
      mainArticle: {
        slug: string
        title: string
        description: string
        category: {
          slug: string
        }
        subcategory: {
          slug: string
        }
        primaryImage: GatsbyImageType
      }
      secondaryArticles: {
        slug: string
        title: string
        description: string
        category: {
          slug: string
        }
        subcategory: {
          slug: string
        }
        primaryImage: GatsbyImageType
      }[]
      careerSubscription: {
        image: GatsbyImageType
        title: string
        description: string
        button: {
          link: string
          name: string
        }
        communityImage: GatsbyImageType
        communityTitle: string
        communityDescription: string
        loginLink: string
        formID: string
      }
      regularSubscription: {
        title: string
        description: string
        image: GatsbyImageType
        nameLink: string
        linkToSubscribe: string
      }
      videoLink: string
    }
    allStrapiArticle: {
      nodes: LatestArticleType[]
    }
  }
};

const Profession = ({ data,pageContext }: ProfessionProps) => {
  const {
    name, bannerDescription, searchPlaceholder, image, links, mainArticle, secondaryArticles,
    careerSubscription, regularSubscription, videoLink,
  } = data.strapiProfession;
  const { subscribeName, subscribeLink } = pageContext;
  const { nodes: articles } = data.allStrapiArticle;
  return (
    <Layout>
      <div className={styles.headingSection}>
        <div className={styles.genericContainer}>
          <div className={styles.bannerContent}>
            <div className={styles.headingText}>
              <div className={styles.titleContainer}>
                <h1 className={styles.title}>{name}</h1>
                <h1 className={styles.title}>Career Hub</h1>
                <ShortUnderline className={styles.underline} />
              </div>
              <p className={styles.description}>{bannerDescription}</p>
              <SearchProfession placeholder={searchPlaceholder || 'Search'} career={name} />
            </div>
            <GatsbyImage
              image={getImage(image.localFile)!}
              alt={name}
              className={styles.headingImage}
            />
          </div>
        </div>
      </div>
      <div className={styles.genericContainer}>
        <div className={styles.linksSection}>
          {links.map((link) => (
            <div className={styles.linkContainer} key={link.name}>
              <a href={link.link} className={styles.linksText}>{link.name}</a>
            </div>
          ))}
        </div>
        <div className={styles.maincontentSection}>
          {mainArticle && (
            <Link to={createArticleUrl(
              mainArticle.category.slug,
              mainArticle.subcategory.slug,
              mainArticle.slug,
            )}
            >
              <GatsbyImage
                className={styles.mainContentImg}
                alt={mainArticle.title}
                image={getImage(mainArticle.primaryImage.localFile)!}
              />
              <div className={styles.mainContentTitle}>{mainArticle.title}</div>
              <div className={styles.maincContentDescription}>{mainArticle.description}</div>
            </Link>
          )}
          {!!secondaryArticles?.length && (
            <div className={styles.otherArticlesSection}>
              {secondaryArticles && secondaryArticles.slice(0, 3).map((item) => {
                const blogImg = getImage(item.primaryImage.localFile);
                return (
                  <Link
                    className={styles.otherArticlesItem}
                    to={createArticleUrl(item.category.slug, item.subcategory.slug, item.slug)}
                    key={item.slug}
                  >
                    {blogImg && (
                      <GatsbyImage
                        className={styles.otherArticlesImg}
                        alt={item.title}
                        image={blogImg}
                      />
                    )}
                    <div>
                      <div className={styles.otherArticlesTitle}>{item.title}</div>
                      <div className={styles.otherArticlesDescription}>{item.description}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className={styles.headingSection}>
        <div className={styles.genericContainer}>
          <div className={styles.professionBannerContent}>
            <div className={styles.professionPlan}>
              <GatsbyImage
                className={styles.professionSubscriptionImg}
                alt={careerSubscription.title}
                image={getImage(careerSubscription.image.localFile)!}
              />
              <div className={styles.subcriptionDynamicText}>
                <span className={styles.communityTitle}>{careerSubscription.title}</span>
                <div className={styles.descriptionContainer}>
                  <span className={styles.communityDescription}>
                    {careerSubscription.description}
                  </span>
                  <Link to={careerSubscription.button.link} className={styles.subscriptionButton}>
                    {careerSubscription.button.name}
                  </Link>
                </div>
              </div>
            </div>
            <div className={styles.professionFormWrapper}>
              <div className={styles.communitySection}>
                <GatsbyImage
                  alt={careerSubscription.communityTitle}
                  image={getImage(careerSubscription.communityImage.localFile)!}
                  className={styles.communityImage}
                />
                <div className={styles.communityText}>
                  <span className={styles.communityTitle}>{careerSubscription.communityTitle}</span>
                  <p className={styles.communityDescription}>
                    {careerSubscription.communityDescription}
                  </p>
                </div>
              </div>
              <div className={styles.formSection}>
                <SmartLink
                  key={subscribeName}
                  ariaLabel={subscribeName}
                  to={subscribeLink}
                  className={styles.buttonStyle}
                >
                  {subscribeName}
                </SmartLink>
                <div className={styles.loginSection}>
                  <span>Already a member?</span>
                  <a href={careerSubscription.loginLink}>Login here</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {articles?.length > 0 && (
        <div className={styles.genericContainer}>
          <div className={styles.latestSection}>
            <LatestArticles latest={articles} />
          </div>
        </div>
      )}
      <SubscriptionBanner
        title={regularSubscription.title}
        description={regularSubscription.description}
        image={getImage(regularSubscription.image.localFile)}
        className={styles.regularSubscriptionWrapper}
        nameLink={subscribeName}
        link={subscribeLink}
      />
      <div className={styles.genericContainer}>
        <div className={styles.featureVideo}>
          <iframe title="Main video" src={videoLink} width="100%" height="auto" />
        </div>
      </div>
    </Layout>
  );
};

export const query = graphql`
  query($slug: String) {
    strapiProfession (slug: {eq: $slug}) {
      name
      bannerDescription
      searchPlaceholder
      image {
        localFile {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
      links {
        name
        link
      }
      mainArticle {
        slug
        title
        description
        category {
          slug
        }
        subcategory {
          slug
        }
        primaryImage {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      secondaryArticles {
        slug
        title
        description
        category {
          slug
        }
        subcategory {
          slug
        }
        primaryImage {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      careerSubscription {
        image {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        title
        description
        button {
          link
          name
        }
        communityImage {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        communityTitle
        communityDescription
        loginLink
        formID
      }
      regularSubscription {
        title
        description
        image {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      videoLink
    }
    allStrapiArticle(
      filter: {relatedProfession: {elemMatch: {slug: {eq: $slug}}}}
      limit: 6
      sort: {updatedAt: DESC}
    ) {
      nodes {
        id
        title
        slug
        description
        primaryImage {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        category {
          slug
        }
        subcategory {
          slug
        }
        updatedAt
      }
    }
  }
`;

export default Profession;
